import React from 'react';
import css from './ListingProfileRight.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faLock, faCheckCircle, faStar } from '@fortawesome/free-solid-svg-icons';
import { injectIntl } from 'react-intl';
import SkyLight from 'react-skylight';
import { getCalendarSyncStatusByProviderId } from '../../../custom-ducks/settings.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import BookingModal from './bookingModal/BookingModal';
import { brandedConfirmAlert } from '../../../components/PopupShell/brandedConfirmAlert';

const showSelfBookingBlocked = message => {
  brandedConfirmAlert({
    title: 'Self-booking not allowed',
    message,
    tone: 'warning',
    buttons: [{ label: 'Got it' }],
  });
};

// Coaches sometimes store an honorific ("Dr.", "Prof.") as part of their
// display name; taking the literal first token would greet the client with
// "Talk to Dr. first" instead of the coach's actual first name.
const NAME_PREFIXES = ['dr', 'dr.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'prof', 'prof.', 'miss'];
function firstNameFrom(displayName) {
  const parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
  const withoutPrefix = parts.length > 1 && NAME_PREFIXES.includes(parts[0].toLowerCase())
    ? parts.slice(1)
    : parts;
  return withoutPrefix[0] || 'the coach';
}

export const ListingProfileRightComponent = props => {
  const {
    isPremium,
    rate,
    name,
    images = [],
    durationSummary,
    sessions,
    format,
    locationShort,
    eventDates,
    handleBookingSubmit,
    currentListing,
    isOwnListing,
    onTalkFirst,
    onGetProviderSyncStatus,
  } = props;

  let simpleDialog;

  const isHourly =
    currentListing.attributes.publicData &&
    currentListing.attributes.publicData.per === 'hourly';

  const priceLabel = isHourly ? 'per hour' : 'per package';
  const firstName = firstNameFrom(name);
  const coverImage =
    images[0] && images[0].attributes.variants['landscape-crop']
      ? images[0].attributes.variants['landscape-crop'].url
      : null;

  const metaLine = [durationSummary, sessions ? `${sessions} sessions` : '', format, locationShort]
    .filter(Boolean)
    .join(' · ');

  const handleBookNow = () => {
    if (isOwnListing) {
      showSelfBookingBlocked(
        "You can't book your own ad. Share this listing's link with a client so they can book it directly."
      );
      return;
    }
    if (isHourly) {
      simpleDialog.show();
    } else {
      handleBookingSubmit({ quantity: 1, currentListing });
    }
  };

  const modelStyle = {
    width: 'min(440px, calc(100vw - 40px))',
    minHeight: 'auto',
    top: '50%',
    left: '50%',
    marginTop: '-230px',
    marginLeft: 'calc(-1 * min(220px, calc(50vw - 20px)))',
    padding: 0,
    borderRadius: '20px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 25px 60px -12px rgba(20, 184, 166, 0.35), 0 0 0 1px rgba(20, 184, 166, 0.08)',
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  const modelOverlayStyle = {
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  };

  return (
    <div className={css.sidebar}>
      {/* Hourly booking modal */}
      <div className={css.modal}>
        <SkyLight
          hideOnOverlayClicked
          ref={ref => (simpleDialog = ref)}
          dialogStyles={modelStyle}
          overlayStyles={modelOverlayStyle}
          closeButtonStyle={{ display: 'none' }}
        >
          <BookingModal
            currentListing={currentListing}
            handleBookingSubmit={handleBookingSubmit}
            onClose={() => simpleDialog.hide()}
            providerId={currentListing.author && currentListing.author.id && currentListing.author.id.uuid}
            onGetProviderSyncStatus={onGetProviderSyncStatus}
          />
        </SkyLight>
      </div>

      {/* ── Booking card ── */}
      <div className={css.bookingCard}>
        {coverImage && (
          <div className={css.coverImageWrap}>
            <img src={coverImage} alt={name} className={css.coverImage} />
            {isPremium && (
              <span className={css.preferredBadgeOverlay}>
                <FontAwesomeIcon icon={faStar} />
                Preferred
              </span>
            )}
          </div>
        )}

        {!coverImage && isPremium && (
          <div className={css.preferredRow}>
            <span className={css.preferredBadge}>
              <FontAwesomeIcon icon={faStar} />
              Preferred
            </span>
          </div>
        )}

        <div className={css.priceSection}>
          <div className={css.priceRow}>
            <span className={css.priceCurrency}>$</span>
            <span className={css.priceValue}>
              {rate != null
                ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : '—'}
            </span>
          </div>
          <p className={css.priceSubtext}>{priceLabel}</p>
          {metaLine && <p className={css.priceMetaLine}>{metaLine}</p>}
        </div>

        <button className={css.bookNowBtn} onClick={handleBookNow}>
          <FontAwesomeIcon icon={faCalendarAlt} />
          Book this experience
        </button>

        <button className={css.talkFirstBtn} onClick={onTalkFirst}>
          Talk to {firstName} first
        </button>

        {eventDates && (
          <div className={css.metaRow}>
            <FontAwesomeIcon icon={faCalendarAlt} className={css.metaIcon} />
            Next dates: {eventDates}
          </div>
        )}

        <div className={css.freeCancellation}>
          <FontAwesomeIcon icon={faCheckCircle} className={css.checkIcon} />
          Free cancellation within 24 hours
        </div>

        <div className={css.secureNote}>
          <FontAwesomeIcon icon={faLock} className={css.lockIcon} />
          Secure booking through Viveka
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  onGetProviderSyncStatus: providerId => dispatch(getCalendarSyncStatusByProviderId(providerId)),
});

const ListingProfileRight = compose(
  connect(null, mapDispatchToProps),
  injectIntl
)(ListingProfileRightComponent);

export default ListingProfileRight;
